import * as React from "react"

interface PullToRefreshConfig {
  onRefresh: () => Promise<void> | void
  threshold?: number
  enabled?: boolean
}

export function usePullToRefresh(config: PullToRefreshConfig) {
  const { onRefresh, threshold = 100, enabled = true } = config
  const [isRefreshing, setIsRefreshing] = React.useState(false)
  const [pullDistance, setPullDistance] = React.useState(0)
  const startY = React.useRef<number>(0)
  const currentY = React.useRef<number>(0)

  const handleTouchStart = React.useCallback((e: TouchEvent) => {
    if (!enabled || window.scrollY > 0) return
    startY.current = e.touches[0].clientY
  }, [enabled])

  const handleTouchMove = React.useCallback((e: TouchEvent) => {
    if (!enabled || window.scrollY > 0 || isRefreshing) return

    currentY.current = e.touches[0].clientY
    const distance = currentY.current - startY.current

    if (distance > 0) {
      e.preventDefault()
      setPullDistance(Math.min(distance, threshold * 1.5))
    }
  }, [enabled, threshold, isRefreshing])

  const handleTouchEnd = React.useCallback(async () => {
    if (!enabled || isRefreshing) return

    if (pullDistance >= threshold) {
      setIsRefreshing(true)
      try {
        await onRefresh()
      } catch (error) {
        console.error('Refresh failed:', error)
      } finally {
        setIsRefreshing(false)
      }
    }
    
    setPullDistance(0)
  }, [enabled, pullDistance, threshold, onRefresh, isRefreshing])

  React.useEffect(() => {
    if (!enabled) return

    document.addEventListener('touchstart', handleTouchStart, { passive: false })
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    document.addEventListener('touchend', handleTouchEnd)

    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
    }
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, enabled])

  return {
    isRefreshing,
    pullDistance,
    shouldTriggerRefresh: pullDistance >= threshold
  }
}